"use client";

import { Upload, Send, FileText, X, Phone, Lock } from "lucide-react";
import React, { useState, useEffect } from "react";

import { useQuery } from "@tanstack/react-query";

import { useSendFax } from "@/app/(authenticated)/fax/_modules/hooks";
import { FaxServices } from "@/app/(authenticated)/fax/_modules/services/services";
import type { SendFaxRequest } from "@/app/(authenticated)/fax/_modules/types/types";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Button } from "@/app/_module/components/ui/button";
import { useFaxAccess } from "@/app/_module/hooks/useFaxAccess";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { showToast } from "@/app/_module/utils/show-toast";
import ViewerDisabled from "@/app/_module/components/ViewerDisabled";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

// Format phone number to (xxx) xxx-xxxx
const formatPhoneNumber = (phoneNumber: string | null): string => {
  if (!phoneNumber) return 'Loading...';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Check if it's a valid US number (10 or 11 digits)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // Remove leading 1 for US numbers
    const digits = cleaned.substring(1);
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  } else if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
  }

  // Return original if not a standard format
  return phoneNumber;
};

export const SendFax = function () {
  const [recipientNumber, setRecipientNumber] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [subject, setSubject] = useState("");
  const [coverPageText, setCoverPageText] = useState("");
  const [includeCoverPage, setIncludeCoverPage] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { sendFax, isLoading: isSending, isSuccess } = useSendFax();
  const { faxNumber, hasAccess, isLoading: isLoadingAccess } = useFaxAccess();

  const { data: dailyUsage } = useQuery({
    queryKey: ["fax-daily-usage"],
    queryFn: () => FaxServices.getDailyUsage(),
    refetchInterval: 60_000,
  });

  // Format and validate recipient phone number input
  const handleRecipientNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');

    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);

    // Format as (xxx) xxx-xxxx
    let formatted = '';
    if (limitedDigits.length > 0) {
      formatted = '(' + limitedDigits.slice(0, 3);
      if (limitedDigits.length > 3) {
        formatted += ') ' + limitedDigits.slice(3, 6);
        if (limitedDigits.length > 6) {
          formatted += '-' + limitedDigits.slice(6, 10);
        }
      }
    }

    setRecipientNumber(formatted);
  };

  // Upload files to Supabase Storage and get URLs
  const uploadFilesToSupabase = async (files: File[]): Promise<{ originalname: string; fileUrl: string; format: string }[]> => {
    const documents = [];

    // Import the storage service and supabase client
    const { default: storageService } = await import('@/app/_module/services/supabaseStorageService');
    const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');

    // Get current user ID for RLS policy compliance
    const supabase = await getSupabaseClient();
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    for (const file of files) {
      try {
        const fileExt = file.name.split('.').pop() || 'pdf';

        // Upload to documents bucket with user-specific path for RLS compliance
        // Path format: {user_id}/fax-documents/{filename}
        const result = await storageService.uploadFile(file, {
          bucket: 'DOCUMENTS',
          folder: `${user.id}/fax-documents`,
          fileName: `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`,
        });

        if (!result.success || !result.data) {
          throw new Error(result.error || `Failed to upload ${file.name}`);
        }

        documents.push({
          originalname: file.name,  // Must match backend DocumentDto field name
          fileUrl: result.data.publicUrl,
          format: fileExt
        });

      } catch (error) {
        throw new Error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    return documents;
  };
  
  // Reset form when fax is sent successfully
  useEffect(() => {
    if (isSuccess) {
      setRecipientNumber("");
      setRecipientName("");
      setCoverPageText("");
      setSubject("");
      setIncludeCoverPage(false);
      setUploadedFiles([]);
    }
  }, [isSuccess]);
  
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    // DEMO SANDBOX: gate at ATTACH, not just at send.
    //
    // handleSendFax is already blocked, but letting a visitor attach first means they pick a
    // file off their own machine before being told no — and that file is a real document
    // they did not need to expose. Refusing at the file picker is both earlier and safer.
    // The seeded fax history shows what a sent fax looks like, with a downloadable sample.
    if (isDemoMode()) {
      // Clear the input so the browser does not keep showing a filename that was refused.
      event.target.value = '';
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Attaching documents is disabled in the demo sandbox. Start a free trial to send real faxes.",
        }),
      );
      return;
    }

    const files = event.target.files;
    if (!files) return;
  
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const validFiles: UploadedFile[] = [];
    const rejectedFiles: string[] = [];
  
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_SIZE) {
        rejectedFiles.push(file.name);
        continue; // Skip oversized files
      }
  
      validFiles.push({
        id: `${Date.now()}-${i}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file,
      });
    }
  
    // Show feedback for rejected files
    if (rejectedFiles.length > 0) {
      const message = `❌ The following files exceed 10MB and were not added:\n${rejectedFiles.join('\n')}`;
      showToast(message);
    }
  
    // Add valid files immediately
    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
    }
  
    // Always reset the input
    event.target.value = "";
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const fileSize = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${fileSize} ${sizes[i]}`;
  };

  const handleSendFax = async () => {
    // DEMO SANDBOX: offer the trial instead of sending.
    //
    // The backend's fax provider would only dry-run this for a demo actor anyway (it
    // reuses Fax.Plus's own mock path), but a fax that silently pretends to send is a
    // worse experience than an honest wall: the visitor has typed a number and attached
    // a file, and "sent!" for a fax that never existed reads as either magic or a lie.
    // Sending faxes also requires a paid Fax.Plus account — precisely the trial pitch.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Sending faxes is disabled in the demo sandbox. Start a free trial to send real faxes.',
        }),
      );
      return;
    }

    // Validate required fields
    if (!recipientNumber || !faxNumber || uploadedFiles.length === 0) {
      showToast("Please fill in all required fields", "error");
      return;
    }

    try {
      setIsUploading(true);

      // Upload files to Supabase Storage first
      const documents = await uploadFilesToSupabase(uploadedFiles.map(f => f.file));

      const totalPages = uploadedFiles.length + (includeCoverPage ? 1 : 0);

      // Convert formatted phone number (xxx) xxx-xxxx to E.164 format +1xxxxxxxxxx
      const digitsOnly = recipientNumber.replace(/\D/g, '');
      const e164Number = `+1${digitsOnly}`;

      // Send fax with DocumentDto[] containing fileUrl
      await sendFax({
        toNumber: e164Number,
        fromNumber: faxNumber,
        documents, // DocumentDto[] with fileUrl
        metadata: { numOfPages: String(totalPages) },
        coverPage: includeCoverPage ? {
          recipientName: recipientName,
          message: coverPageText,
          subject: subject
        } : undefined,
      });

    } catch (error) {
      showToast(`Failed to send fax: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const totalPages = uploadedFiles.length + (includeCoverPage ? 1 : 0);
  const isAtDailyLimit = dailyUsage ? dailyUsage.remaining <= 0 : false;
  const canSend =
    Boolean(recipientNumber) &&
    Boolean(faxNumber) &&
    uploadedFiles.length > 0 &&
    !isSending &&
    !isUploading &&
    !isAtDailyLimit;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Fax
            </CardTitle>
            {dailyUsage && (
              <span className={`text-sm font-medium ${dailyUsage.remaining <= 10 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {dailyUsage.sent}/{dailyUsage.limit} sent today
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Daily limit warning */}
          {isAtDailyLimit && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-700">
                You have reached your daily fax limit of {dailyUsage?.limit} faxes. Your limit resets at midnight.
              </AlertDescription>
            </Alert>
          )}

          {/* Recipient Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="recipient-number">Recipient Fax Number *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  className="pl-10"
                  id="recipient-number"
                  onChange={handleRecipientNumberChange}
                  placeholder="(555) 123-4567"
                  type="tel"
                  value={recipientNumber}
                  maxLength={14}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="from-number">From Fax Number *</Label>
              <div className="relative">
                <Input
                  id="from-number"
                  value={formatPhoneNumber(faxNumber)}
                  disabled={true}
                  className="bg-gray-50 cursor-not-allowed pr-10"
                  type="tel"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Lock className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This is your dedicated fax number assigned to your subscription
              </p>
            </div>
          </div>

          {/* Document Upload */}
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Documents to Fax *</div>
              <div className="mt-2">
                <div className="flex items-center justify-center w-full">
                  <div
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                    onClick={() => document.getElementById('file-upload')?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        document.getElementById('file-upload')?.click();
                      }
                    }}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-4 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF, DOC, DOCX, TXT (MAX. 10MB per file)
                      </p>
                    </div>
                    <input
                      multiple
                      accept=".pdf,.doc,.docx,.txt"
                      className="hidden"
                      disabled={isUploading}
                      id="file-upload"
                      onChange={handleFileUpload}
                      type="file"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Uploaded Documents ({uploadedFiles.length})</div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <Button
                        className="h-6 w-6 p-0"
                        onClick={() => removeFile(file.id)}
                        size="sm"
                        variant="ghost"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Cover Page (UI only; not sent yet) */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                checked={includeCoverPage}
                className="rounded border-gray-300"
                id="include-cover"
                onChange={(e) => setIncludeCoverPage(e.target.checked)}
                type="checkbox"
              />
              <Label htmlFor="include-cover">Include Cover Page</Label>
            </div>

            {!!includeCoverPage && (
              <React.Fragment>
                <div>
                  <Label htmlFor="recipient-name">Recipient Name</Label>
                  <Input
                    id="recipient-name"
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="John Smith"
                    value={recipientName}
                  />
                </div>
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Fax Subject"
                    value={subject}
                  />
                </div>
                <div>
                  <Label htmlFor="cover-text">Cover Page Message</Label>
                  <Textarea
                    id="cover-text"
                    onChange={(e) => setCoverPageText(e.target.value)}
                    placeholder="Enter your message for the cover page..."
                    rows={3}
                    value={coverPageText}
                  />
                </div>
              </React.Fragment>
            )}
          </div>

          {/* Summary */}
          {!!(uploadedFiles.length > 0 || includeCoverPage) && (
            <Alert>
              <AlertDescription>
                Total pages to fax: <strong>{totalPages}</strong>
                {totalPages > 1 &&
                  ` (${uploadedFiles.length} document${uploadedFiles.length !== 1 ? "s" : ""
                  }${includeCoverPage ? " + 1 cover page" : ""})`}
              </AlertDescription>
            </Alert>
          )}

          {/* Send Button */}
          <ViewerDisabled message="Upgrade to send faxes">
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              disabled={!canSend}
              onClick={handleSendFax}
              size="lg"
            >
              {(isSending || isUploading) ? (
                <React.Fragment>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {isUploading ? 'Uploading Files...' : 'Sending Fax...'}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Send className="h-4 w-4 mr-2" />
                  Send Fax
                </React.Fragment>
              )}
            </Button>
          </ViewerDisabled>
        </CardContent>
      </Card>
    </div>
  );
};